/**
 * The `tab.start` snapshot (P2 design §4.7): the state a tab started in — what a replay
 * restores before its first step, and what a bug report carries beside its rings. Ids only:
 * no question text, no names.
 */
import { routeTemplateOf } from "../view/routeTemplate";

/** Most forecast ids a snapshot lists. */
export const TAB_START_MAX_FORECASTS = 50;

/** /api/me string fields recorded as flags beside the booleans. */
const STRING_FLAGS = ["preprocessVariant", "sourceSpace", "spaceTier"];

/**
 * The identity and evaluated flags in an /api/me answer: `{user, org, flags}`. `user` is the
 * Zitadel sub, `org` chatboard's organisation id; `flags` holds every top-level boolean (the
 * evaluated feature gates) and a few short string variants.
 */
export function telemetryIdentityFromMe(me) {
  const flags = {};
  if (me && typeof me === "object") {
    for (const [k, v] of Object.entries(me)) {
      if (typeof v === "boolean") flags[k] = v;
    }
    for (const k of STRING_FLAGS) {
      if (typeof me[k] === "string" && me[k].length <= 64) flags[k] = me[k];
    }
  }
  const user = typeof me?.user?.sub === "string" && me.user.sub ? me.user.sub : null;
  // `orgId` is the session's organisation, present in a signed-in answer even when the
  // name lookup behind `org` failed (org: null); older answers fall back to `org.id`.
  const rawOrg = me && typeof me === "object" && "orgId" in me ? me.orgId : me?.org?.id;
  const org = rawOrg != null && rawOrg !== "" ? String(rawOrg) : null;
  return { user, org, flags };
}

const idOf = (f) => (f && typeof f.id === "string" && f.id ? f.id : null);

/**
 * Build the `tab.start` event. `app` is the app's own state (hooks/useTelemetryAppState.js):
 * `{engine, asOf, forecasts, selectedId, debugOverrides}`; missing parts are null.
 */
export function buildTabStart({
  href,
  viewportW,
  viewportH,
  dpr,
  flags,
  app,
  timezone,
  locale,
  buildSha,
  registryVersion,
}) {
  const forecasts = Array.isArray(app?.forecasts) ? app.forecasts : [];
  const ids = [];
  for (const f of forecasts) {
    const id = idOf(f);
    if (id && !ids.includes(id)) ids.push(id);
    if (ids.length >= TAB_START_MAX_FORECASTS) break;
  }
  const overrides = {};
  for (const [k, v] of Object.entries(app?.debugOverrides || {})) {
    if (v !== null && v !== undefined && v !== "" && v !== false) overrides[k] = v;
  }
  return {
    event: "tab.start",
    route: routeTemplateOf(href),
    viewport_w: Number.isFinite(viewportW) ? Math.round(viewportW) : null,
    viewport_h: Number.isFinite(viewportH) ? Math.round(viewportH) : null,
    dpr: Number.isFinite(dpr) ? dpr : null,
    flags: flags || {},
    debug_overrides: overrides,
    // "" until forecast health answers: no engine yet, not an engine named "".
    engine: typeof app?.engine === "string" && app.engine ? app.engine : null,
    as_of: typeof app?.asOf === "string" && app.asOf ? app.asOf : null,
    forecast_ids: ids,
    forecast_count: forecasts.length,
    selected_forecast_id:
      typeof app?.selectedId === "string" && app.selectedId ? app.selectedId : null,
    timezone: timezone || null,
    locale: locale || null,
    build_sha: buildSha || null,
    registry_version: registryVersion || null,
  };
}
